import type { PieceButton, PieceButtonHole, SceneEntity } from '@seddi/scene';

import { PropsDiff } from '../core/reconciler/propsDiff';
import { findButton } from '../domain/scene/scene';
import {
  type PieceButtonRenderable,
  attachButton,
  attachGarmentGeometry,
  detachGarmentGeometry,
  isCloseToHole,
  updateStitchesColor,
  updateTransformFromPiece,
} from '../renderables/pieceButtonRenderable';
import type { PieceRenderable } from '../renderables/pieceRenderable';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextState - Next state.
 * @param prevState - Previous state.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update1st = (
  renderable: PieceButtonRenderable,
  nextState: PieceButton,
  prevState: PieceButton | undefined,
  pieceRenderable: PieceRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const {
    config,
    nextGarmentGeometry,
    nextScene,
    nextUseGarmentGeometry,
    prevGarmentGeometry,
    prevUseGarmentGeometry,
    resources,
  } = frame;

  let dirty = false;

  // Update from garment geometry.
  if (nextGarmentGeometry !== prevGarmentGeometry) {
    if (prevGarmentGeometry) {
      detachGarmentGeometry(renderable);
    }
    if (nextGarmentGeometry) {
      attachGarmentGeometry(renderable, nextState, nextGarmentGeometry, resources);
    }
    dirty = true;
  }

  const diffProps = PropsDiff.diff(nextState, prevState);

  // Update resource.
  if (diffProps.includes('resourceID')) {
    const id = nextState.resourceID;

    if (resources.findButton(id)) {
      attachButton(renderable, nextState, resources);
      dirty = true;
    } else {
      if (!resources.hasButtonRequest(id)) {
        const b = findButton(nextScene, id);
        resources.loadButtonDeferred(b);
      }
    }
  }

  // Update transform.
  const isTransformDirty =
    !nextUseGarmentGeometry &&
    ((!nextUseGarmentGeometry && prevUseGarmentGeometry) ||
      frame.entities.get(pieceRenderable.id)!.isGeometryDirty ||
      diffProps.includes('resourceID') ||
      diffProps.includes('transform'));

  if (isTransformDirty) {
    // Update transform depends on piece geometry and button height.
    const nextButton = findButton(nextScene, nextState.resourceID)!;
    updateTransformFromPiece(renderable, nextState, nextButton, pieceRenderable);
    dirty = true;
  }

  // Update stitches.
  if (!config.hideStitches && nextGarmentGeometry && diffProps.includes('recolorThread')) {
    updateStitchesColor(renderable, nextState.recolorThread!, resources);
  }

  return dirty;
};

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextState - Button next.
 * @param prevState - Button prev.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update2nd = (
  renderable: PieceButtonRenderable,
  nextState: PieceButton,
  pieceRenderable: PieceRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const { nextScene, nextUseGarmentGeometry } = frame;
  // Do nothing if garment geometry.
  if (nextUseGarmentGeometry) {
    return false;
  }

  // Do nothing if no dirty pair.
  const pair = nextState.pair;
  const pairData = pair && findPair(pair, frame);
  if (!pairData) {
    return false;
  }

  const pairEntity = pairData.entity;
  const pairHole = pairData.hole;
  const pairRenderable: PieceRenderable = frame.scene.entities.get(pairEntity._id)!.piece;
  if (isCloseToHole(pieceRenderable, nextState.transform, pairRenderable, pairHole.transform)) {
    const resource = findButton(nextScene, nextState.resourceID)!;
    updateTransformFromPiece(renderable, pairHole, resource, pairRenderable);
  }
};

// Detail

type HoleData = { entity: SceneEntity; hole: PieceButtonHole };

/**
 * Find hole by ID.
 * @param id - Piece hole ID.
 * @param frame - Reconciler frame.
 */
const findPair = (id: string, frame: SceneReconcilerFrame): HoleData | undefined => {
  for (const e of frame.entities.values()) {
    if (!e.isGeometryDirty || !e.isPiece) {
      continue;
    }
    // Find corresponding hole.
    const entity = e.data;
    const hole = entity.components.garmentPiece.buttonHoles?.find((h) => h._id === id);
    if (hole) {
      return { entity, hole };
    }
  }
  return undefined;
};
